<template>
  <div :class="$style.page">
    <BaseContainer :class="$style.container">
      <form @submit.prevent="submit">
        <BaseLoader
          :class="$style.box"
          :is-active="isFormSending"
        >
          <FormBox
            v-if="(totalUsers !== null) && (totalUsers < user.company.add_users_limit)"
            :heading="formStepData.heading"
            :lead="formStepData.lead"
          >
            <template v-if="formStep === 1">
              <BaseField
                is-required
                :error="emailError"
                :class="$style.field"
              >
                <template #label>
                  EMAIL
                </template>

                <BaseInput
                  v-model="email"
                  type="email"
                  :has-error="Boolean(emailError)"
                />
              </BaseField>
              <BaseField
                is-required
                :error="givenNameError"
                :class="$style.field"
              >
                <template #label>
                  GIVEN NAME
                </template>

                <BaseInput
                  v-model="givenName"
                  :has-error="Boolean(givenNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :error="familyNameError"
                :class="$style.field"
              >
                <template #label>
                  FAMILY NAME
                </template>

                <BaseInput
                  v-model="familyName"
                  :has-error="Boolean(familyNameError)"
                />
              </BaseField>
              <BaseField
                is-required
                :class="$style.field"
                :error="timeZoneError"
              >
                <template #label>
                  TIME ZONE
                </template>

                <BaseSelect
                  v-model="timeZone"
                  :class="$style.multiselect"
                  :has-error="Boolean(timeZoneError)"
                >
                  <option disabled>
                    Select
                  </option>
                  <option
                    v-for="option in $options.TIME_ZONES_OPTIONS"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </BaseSelect>
              </BaseField>

              <BaseField
                v-if="isAdmin"
                is-required
                :class="$style.field"
                :error="companyIdError"
              >
                <template #label>
                  COMPANY
                </template>

                <BaseSelect
                  v-model="companyId"
                  :class="$style.multiselect"
                  :has-error="Boolean(companyIdError)"
                >
                  <option disabled>
                    Select
                  </option>
                  <option
                    v-for="option in companies"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </BaseSelect>
              </BaseField>
            </template>

            <template #footer>
              <BaseButton
                variant="success"
                type="submit"
                is-block
              >
                {{ formStepData.submitText }}
              </BaseButton>
            </template>
          </FormBox>
          <FormBox
            v-if="(totalUsers !== null) && (totalUsers >= user.company.add_users_limit)"
            heading="You have reached the limit of number of users you can add"
          />
        </BaseLoader>
      </form>
    </BaseContainer>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import { mapGetters, mapState } from 'vuex';
import BaseContainer from '@/components/base/Container';
import BaseField from '@/components/base/Field';
import BaseInput from '@/components/base/Input';
import BaseButton from '@/components/base/Button';
import BaseSelect from '@/components/base/Select';
import BaseLoader from '@/components/base/Loader';
import FormBox from '@/components/FormBox';
import { adminQueries, companyAdminQueries } from '@/aws-api';
import { timeZones } from '@/config';

export default {
  components: {
    BaseContainer,
    BaseField,
    BaseInput,
    BaseButton,
    BaseSelect,
    BaseLoader,
    FormBox,
  },
  TIME_ZONES_OPTIONS: timeZones,
  FORM_STEPS_DATA: [
    {
      heading: 'Add new user',
      submitText: 'Add user',
    },
    {
      heading: 'Invite sent!',
      lead: 'They’ll receive an email shortly with their invite details included.',
      submitText: 'Go to users',
    },
  ],
  apollo: {
    companies: {
      query: gql`
        query {
          companies {
            id
            name
          }
        }
      `,
      skip() {
        return !this.isAdmin;
      },
    },
    totalUsers: {
      query: gql`
        query ($companyId: Int!) {
          users_aggregate(where: {
            suspended: {_eq: false},
            company_id: {_eq: $companyId},
          }) {
            aggregate {
              count
            }
          }
        }
      `,
      variables() {
        return {
          companyId: this.user.company.id,
        };
      },
      update(data) {
        if (this.isAdmin) return 0;
        return data.users_aggregate.aggregate.count;
      },
    },
  },
  data() {
    return {
      companies: [],
      givenName: '',
      givenNameError: null,
      familyName: '',
      familyNameError: null,
      email: '',
      emailError: null,
      timeZone: null,
      timeZoneError: null,
      companyId: null,
      companyIdError: null,
      isFormSending: false,
      formStep: 1,
      totalUsers: null,
    };
  },
  computed: {
    ...mapGetters('user', ['isAdmin']),
    ...mapState('user', ['user']),
    formStepData() {
      return this.$options.FORM_STEPS_DATA[this.formStep - 1];
    },
  },
  methods: {
    async addUser() {
      this.emailError = null;
      this.givenNameError = null;
      this.familyNameError = null;
      this.timeZoneError = null;
      this.companyIdError = null;
      let hasError = false;

      if (this.email.trim() === '') {
        hasError = true;
        this.emailError = 'Can\'t be empty';
      }

      if (this.givenName.trim() === '') {
        hasError = true;
        this.givenNameError = 'Can\'t be empty';
      }

      if (this.familyName.trim() === '') {
        hasError = true;
        this.familyNameError = 'Can\'t be empty';
      }

      if (this.timeZone == null) {
        hasError = true;
        this.timeZoneError = 'Can\'t be empty';
      }

      if (this.isAdmin && this.companyId == null) {
        hasError = true;
        this.companyIdError = 'Can\'t be empty';
      }

      if (hasError) {
        return;
      }

      this.isFormSending = true;
      try {
        const { user } = await (
          this.isAdmin
            ? adminQueries
            : companyAdminQueries
        ).createUser(this.email, this.givenName, this.familyName);

        try {
          await this.$apollo.mutate({
            mutation: gql`
              mutation ($companyId: Int!, $userId: String!, $timeZone: String) {
                insert_users_one(object: {company_id: $companyId, id: $userId, time_zone: $timeZone}) {
                  id
                }
              }
            `,
            variables: {
              userId: user.Username,
              timeZone: this.timeZone,
              companyId: this.isAdmin ? this.companyId : this.user.company.id,
            },
          });

          this.formStep = 2;
        } catch (e) {
          console.error(e); // eslint-disable-line no-console
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.emailError = e.response.data.message;
      }
      this.isFormSending = false;
    },
    submit() {
      switch (this.formStep) {
        case 1:
          return this.addUser();
        case 2:
          return this.$router.push({ name: 'Users' });
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
  background: #fafafa;
}

.container {
  display: flex;
  justify-content: center;
}

.box {
  width: 470px;
}

.field {

  &:not(:first-child) {
    margin-top: 20px;
  }
}

.multiselect {
  width: 100%;
}
</style>
